/**
 * useSyncOnReconnect — listens for offline queue flush events and shows
 * toast feedback when queued mutations are synced or fail.
 *
 * Mount once in the app layout (next to startOfflineQueue).
 */

import { useEffect, useState, useCallback } from 'react'
import {
  onQueueChange,
  onConflict,
  onFlushResult,
  flushQueue,
  getPendingCount,
  type ConflictEvent,
} from '@appic/lib/offline/offlineQueue'

interface UseSyncOnReconnectOptions {
  /** Called when items are synced. Receives count of synced items. */
  onSynced?: (count: number) => void
  /** Called when items fail to sync after max retries. */
  onFailed?: (count: number) => void
  /** Called when a conflict is detected (server data newer than local). */
  onConflict?: (event: ConflictEvent) => void
}

interface UseSyncOnReconnectResult {
  /** Number of pending items in the queue */
  pendingCount: number
  /** Whether a flush is in progress */
  isSyncing: boolean
  /** Manually trigger a flush */
  sync: () => Promise<void>
}

export function useSyncOnReconnect(
  options: UseSyncOnReconnectOptions = {},
): UseSyncOnReconnectResult {
  const { onSynced, onFailed, onConflict: onConflictCb } = options
  const [pendingCount, setPendingCount] = useState(0)
  const [isSyncing, setIsSyncing] = useState(false)

  // Track pending count
  useEffect(() => {
    getPendingCount().then(setPendingCount)
    const unsub = onQueueChange(setPendingCount)
    return unsub
  }, [])

  // Listen for conflicts
  useEffect(() => {
    if (!onConflictCb) return
    const unsub = onConflict(onConflictCb)
    return unsub
  }, [onConflictCb])

  // D-33 R1 — surface results of EVERY flush, including the queue's own
  // online-event + interval auto-flushes (previously fire-and-forget). Without
  // this, a sync that failed outside this hook's own `online` handler reached
  // no banner. We only fan out synced/failed signals here; the count itself is
  // tracked via onQueueChange above.
  useEffect(() => {
    if (!onSynced && !onFailed) return
    const unsub = onFlushResult((result) => {
      if (result.synced > 0) onSynced?.(result.synced)
      if (result.failed > 0) onFailed?.(result.failed)
    })
    return unsub
  }, [onSynced, onFailed])

  // Auto-flush on reconnect with toast feedback
  useEffect(() => {
    const handleOnline = async () => {
      const countBefore = await getPendingCount()
      if (countBefore === 0) return

      setIsSyncing(true)
      const result = await flushQueue()
      setIsSyncing(false)

      if (result.synced > 0) onSynced?.(result.synced)
      if (result.failed > 0) onFailed?.(result.failed)
    }

    window.addEventListener('online', handleOnline)
    return () => window.removeEventListener('online', handleOnline)
  }, [onSynced, onFailed])

  const sync = useCallback(async () => {
    setIsSyncing(true)
    const result = await flushQueue()
    setIsSyncing(false)
    if (result.synced > 0) onSynced?.(result.synced)
    if (result.failed > 0) onFailed?.(result.failed)
  }, [onSynced, onFailed])

  return { pendingCount, isSyncing, sync }
}
